import { JsonObject } from 'ta-json';
import {
  GetAllRetailers_allRetailers_edges_node_retailerOptions,
  GetAllRetailers_allRetailers_edges_node_retailerOptions_installationSKUs,
} from '@src/app/generated/GetAllRetailers';
import { DoubleSKUInputMap, DoubleSKUInputPair, RetailerOptionsInput } from '@src/app/generated/globalTypes';
import { ColorCodes, Link, RetailerOptions, WoodTypeAvailable } from '@ess/jg-rule-executor';
import { PortalColorCodes } from '@src/app/model/portal-color-codes';
import { PortalLink } from '@src/app/model/portal-link';
import { PortalWoodTypeAvailable } from '@src/app/model/portal-wood-type';
import { PortalHelpItem } from '@src/app/model/portal-help-item';
import { WebhookFlowType } from '@ess/jg-rule-executor/dist/src/domain/webhook-flow-type';
import { PortalWebhookOptions } from '@src/app/model/portal-webhook-options';
import { PortalWebhookUrls } from '@src/app/model/portal-webhook-urls';
import { PortalConfiguratorOptions } from '@src/app/model/portal-configurator-options';
import { PortalEanOptions } from '@src/app/model/portal-ean-options';
import { PortalCurrencyOptions } from '@src/app/model/portal-currency-options';
import { PortalHornbachOptions } from '@src/app/model/portal-hornbach-options';

/**
 * Retailer options
 */
@JsonObject()
export class PortalRetailerOptions extends RetailerOptions {
  public installationSKUs: DoubleSKUInputMap;

  constructor(
    purchasePriceMandatory: boolean,
    currencyOptions: PortalCurrencyOptions,
    colorCodes: ColorCodes,
    agreementLinks: Link[] = undefined,
    helpLink: Link = undefined,
    woodTypesAvailable: WoodTypeAvailable[] = undefined,
    configurationIdentifier: string = undefined,
    resultSceneSkyMaterialReference: string = undefined,
    resultSceneFloorMaterialReference: string = undefined,
    installationSKUs: DoubleSKUInputMap = undefined,
    preconfigurations: string[] = [],
    helpItems: PortalHelpItem[] = [],
    webhookOptions: PortalWebhookOptions = new PortalWebhookOptions(
      WebhookFlowType.codegetredirect, new PortalWebhookUrls('')
    ),
    codeParameterName: string = undefined,
    configuratorOptions: PortalConfiguratorOptions = undefined,
    eanOptions: PortalEanOptions = undefined,
    hornbachOptions: PortalHornbachOptions = undefined,
  ) {
    super(
      purchasePriceMandatory, currencyOptions, colorCodes, agreementLinks,
      helpLink, woodTypesAvailable, configurationIdentifier, resultSceneSkyMaterialReference,
      resultSceneFloorMaterialReference, undefined,
      preconfigurations, helpItems, webhookOptions, codeParameterName, configuratorOptions, eanOptions, hornbachOptions
    );
    this.installationSKUs = installationSKUs;
  }

  static fromGql(gql: GetAllRetailers_allRetailers_edges_node_retailerOptions): PortalRetailerOptions {
    return new PortalRetailerOptions(
      gql.purchasePriceMandatory,
      gql.currencyOptions ? PortalCurrencyOptions.fromGql(gql.currencyOptions) : undefined,
      PortalColorCodes.fromGql(gql.colorCodes),
      gql.agreementLinks.map(PortalLink.fromGql),
      gql.helpLink ? PortalLink.fromGql(gql.helpLink) : null,
      gql.woodTypesAvailable.map(PortalWoodTypeAvailable.fromGql),
      gql.configurationIdentifier,
      gql.resultSceneSkyMaterialReference,
      gql.resultSceneFloorMaterialReference,
      gql.installationSKUs ? PortalRetailerOptions.installationSKUsFromInput(gql.installationSKUs) : undefined,
      gql.preconfigurations,
      gql.helpItems ? gql.helpItems.map(h => PortalHelpItem.fromGql(h)) : [],
      gql.webhookOptions
        ? PortalWebhookOptions.fromGql(gql.webhookOptions)
        : new PortalWebhookOptions(WebhookFlowType.codegetredirect, new PortalWebhookUrls('')),
      gql.codeParameterName,
      gql.configuratorOptions ? PortalConfiguratorOptions.fromGql(gql.configuratorOptions) : undefined,
      gql.eanOptions ? PortalEanOptions.fromGql(gql.eanOptions) : undefined,
      gql.hornbachOptions ? PortalHornbachOptions.fromGql(gql.hornbachOptions) : undefined,
    );
  }

  static installationSKUsFromInput(
    input: GetAllRetailers_allRetailers_edges_node_retailerOptions_installationSKUs
  ): DoubleSKUInputMap {
    return {
      values: input.values.map(v => ({ key: v.key, value: v.value } as DoubleSKUInputPair)),
    } as DoubleSKUInputMap;
  }

  toGqlInput(): RetailerOptionsInput {
    return {
      currencyOptions: (this.currencyOptions as PortalCurrencyOptions)?.toGqlInput(),
      colorCodes: (this.colorCodes as PortalColorCodes).toGqlInput(),
      agreementLinks: this.agreementLinks?.map(link => (link as PortalLink).toGqlInput()) ?? [],
      helpLink: (this.helpLink as PortalLink)?.toGqlInput(),
      woodTypesAvailable: this.woodTypesAvailable ?? [],
      configurationIdentifier: this.configurationIdentifier ?? '',
      resultSceneSkyMaterialReference: this.resultSceneSkyMaterialReference,
      resultSceneFloorMaterialReference: this.resultSceneFloorMaterialReference,
      installationPrices: this.installationSKUs,
      webhookOptions: (this.webhookOptions as PortalWebhookOptions).toGqlInput(),
      purchasePriceMandatory: this.purchasePriceMandatory,
      preconfigurations: this.preconfigurations,
      helpItems: (this.helpItems as PortalHelpItem[])?.map(t => t.toGqlInput()),
      codeParameterName: this.codeParameterName,
      configuratorOptions: (this.configuratorOptions as PortalConfiguratorOptions)?.toGqlInput()
        || new PortalConfiguratorOptions().toGqlInput(),
      eanOptions: (this.eanOptions as PortalEanOptions)?.toGqlInput()
        || new PortalEanOptions(false).toGqlInput(),
      hornbachOptions: (this.hornbachOptions as PortalHornbachOptions)?.toGqlInput(),
    };
  }
}
